import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  ApplicationAnswers,
  ApplicationContentInput,
  CheckboxField,
  Field,
  FieldType,
  TextInputType,
} from '@bybeam/platform-types';
import { isNewDocument } from '@platform-ui-common/components/DocumentUpload/index.js';
import { GenericFormData } from '@platform-ui-common/hooks/useForm/index.js';
import { getUnhousedKey } from '@platform-ui-common/utilities/address.js';
import replacePlaceholders, {
  PlaceholderSource,
} from '@platform-ui-common/utilities/placeholder.js';
import { TemporaryDocument } from '@platform-ui/@types/document.js';
import { evaluate } from '../ast/evaluate.js';
import mergeUpdates from '../mergeUpdates.js';

export * from './form.js';
export * from './types.js';
export * from './value.js';

const KEY_SEPARATOR = '.';

// Given a field key, which is expected to be a nested path through the answers object,
// returns the immediate parent key, meaning the same key removing the latest .[VALUE] part.
//
// For example, for key 'one.two.three' it would return 'one.two'
export function getFieldParentKey(key: string): string {
  const keys = key.split(KEY_SEPARATOR);

  return keys.slice(0, keys.length - 1).join(KEY_SEPARATOR);
}

const ADDRESS_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'zip'];

export function getUnhousedField(key: string): CheckboxField {
  return {
    type: FieldType.Checkbox,
    key: getUnhousedKey(key),
    copy: 'I currently don’t have a street address',
  };
}

// Given the useForm data for the field, returns the expected value for the field
// in the answers. In most cases this is just the value of the key in the form data,
// but there may be exceptions (e.g., radio group fields are flattened in the form
// data to one key per option; in the answers they're expected to be nested in an object).
export function getFieldFormValue<FormData extends GenericFormData>(
  field: Field,
  update: FormData,
  // biome-ignore lint/suspicious/noExplicitAny: reason
): any {
  switch (field.type) {
    case FieldType.Address: {
      const isUnhoused = !!update[getUnhousedKey(field.key)];
      return {
        address: Object.fromEntries(
          ADDRESS_FIELDS.filter(
            (field) => !isUnhoused || !['addressLine1', 'addressLine2'].includes(field),
          ).map((key) => [key.split('.').slice(-1)[0], update[`${field.key}.${key}`]]),
        ),
        copied: !!update[`${field.key}.copied`],
        isUnhoused,
      };
    }
    // biome-ignore lint/suspicious/noFallthroughSwitchClause: intentionally fall through for non-currency
    case FieldType.Text:
      if (field.inputType === TextInputType.Currency) return Number(update[field.key]) * 100;
    // Fall through
    case FieldType.Complex:
    case FieldType.Checkbox:
    case FieldType.CheckboxGroup:
    case FieldType.Date:
    case FieldType.Dropdown:
    case FieldType.Document:
    case FieldType.RadioList:
      return update[field.key];
    case FieldType.Calculated:
      if (field.display) {
        return update[field.key];
      }
      return evaluate(field.formula, update);

    case FieldType.Typography:
      throw new Error(`Field type ${field.type} has no value!`);
    default:
      return assertExhaustive(field);
  }
}

// Given the useForm data for the field, returns the (deep) partial of the answers to be
// updated. This is constructed using the path specified in the key.
export function getFieldUpdate<FormData extends GenericFormData>(
  field: Field,
  update: FormData,
): Partial<ApplicationContentInput> {
  // biome-ignore lint/suspicious/noExplicitAny: reason
  const toAnswers = (value: any): Partial<ApplicationAnswers> =>
    field.key.split('.').reduceRight((answers, key) => ({ [key]: answers }), value);

  switch (field.type) {
    case FieldType.Address: {
      const { address, copied, isUnhoused } = getFieldFormValue(field, update);
      return {
        addresses: [
          {
            key: `${field.key}Id`,
            ...(copied ? { copyKey: `${field.copyAddressKey}Id` } : { address }),
          },
        ],
        ...(field.allowUnhoused && { answers: toAnswers({ isUnhoused }) }),
      };
    }
    case FieldType.Document: {
      const documents = getFieldFormValue(field, update) ?? [];
      const existingDocuments = documents
        ?.filter((doc: TemporaryDocument) => doc && !isNewDocument(doc))
        .map(({ id }) => id);
      const files = documents?.filter(isNewDocument).map(({ file }) => file);
      return {
        answers: toAnswers(existingDocuments),
        ...(files.length > 0 && { documents: [{ key: field.key, files }] }),
      };
    }
    case FieldType.Typography:
      return {};
    case FieldType.Complex: {
      const items = getFieldFormValue(field, update);

      const updates = items
        .map((item) =>
          mergeUpdates(field.subFields.map((subField) => getFieldUpdate(subField, item))),
        )
        .map(({ addresses, documents, ...update }, idx) => {
          const affixParentKey = <T extends { key: string }>(updates: T[]): T[] =>
            updates.map((update) => ({ ...update, key: [field.key, idx, update.key].join('.') }));
          return {
            addresses: affixParentKey(addresses ?? []),
            documents: affixParentKey(documents ?? []),
            ...update,
          };
        });

      return {
        ...mergeUpdates(updates),
        answers: toAnswers(updates.map(({ answers }) => answers)),
      };
    }
    default:
      return {
        answers: toAnswers(getFieldFormValue(field, update)),
      };
  }
}

export function replaceCopyPlaceholders<T extends Field>(
  field: T,
  placeholders: PlaceholderSource,
): T {
  // More jest being unhappy about the types here...
  const fieldHasCopy = field as Field;
  if (
    !(
      ('copy' in fieldHasCopy && fieldHasCopy.copy) ||
      ('display' in fieldHasCopy && fieldHasCopy.display)
    )
  )
    return field;

  switch (fieldHasCopy.type) {
    case FieldType.Calculated:
      return {
        ...fieldHasCopy,
        display: {
          ...fieldHasCopy.display,
          copy: replacePlaceholders(fieldHasCopy.display.copy, placeholders),
        },
      } as T;
    case FieldType.Document:
      return {
        ...fieldHasCopy,
        copy: {
          ...(fieldHasCopy.copy?.title && {
            title: replacePlaceholders(fieldHasCopy.copy.title, placeholders),
          }),
          ...(fieldHasCopy.copy?.description && {
            description: replacePlaceholders(fieldHasCopy.copy.description, placeholders),
          }),
        },
      } as T;
    default:
      return { ...fieldHasCopy, copy: replacePlaceholders(fieldHasCopy.copy, placeholders) } as T;
  }
}

export function getFieldLabel(field: Field): string {
  switch (field.type) {
    case FieldType.Calculated:
      return field.display?.copy;
    case FieldType.Document:
      return field.copy?.title;
    default:
      return 'copy' in field ? field.copy : undefined;
  }
}

export function isFieldEnabled(
  field: Field,
  answers: Partial<ApplicationAnswers> | GenericFormData,
): boolean {
  return !field.dynamicLogic || evaluate(field.dynamicLogic, answers);
}
